import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { ChonkyButton } from '@/components/shared/chonky-button'

import { useCreatorPayouts } from '@/hooks/use-creators'
import type { CreatorPayout } from '@/lib/api/types'
import { CreatorPayoutDialog } from './creator-payout-dialog'

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  pending_invoice: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  invoice_received: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  pending_invoice: 'Aguardando NF',
  invoice_received: 'NF Recebida',
  processing: 'Em Pagamento',
  paid: 'Pago',
  cancelled: 'Cancelado',
}

interface CreatorPayoutsTableProps {
  creatorId: string
}

/**
 * Render a paginated payout history table with controls to generate a payout for a given creator.
 *
 * Displays loading skeletons, an empty state with an action to create the first payout, a table of
 * payout entries (period, sales, commissions, platform fees, net amount, status), pagination when
 * applicable, and a dialog to create new payouts.
 *
 * @param creatorId - The creator's identifier used to fetch and manage payouts
 * @returns The component's UI that presents the creator's paginated payout history and payout dialog
 */
export function CreatorPayoutsTable({ creatorId }: CreatorPayoutsTableProps) {
  const [page, setPage] = useState(1)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const limit = 5

  const { data: payoutsResult, isLoading } = useCreatorPayouts({
    creatorId,
    page,
    limit,
  })

  const payouts =
    (payoutsResult as { data?: CreatorPayout[] })?.data ||
    (payoutsResult as unknown as CreatorPayout[]) ||
    []
  const meta = (payoutsResult as { meta?: { totalPages: number } })?.meta

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    )
  }

  if (payouts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground border rounded-lg border-dashed">
        <p>Nenhum histórico de fechamento de comissão.</p>
        <ChonkyButton variant="secondary" className="mt-4" onClick={() => setIsDialogOpen(true)}>
          Gerar Primeiro Payout
        </ChonkyButton>
        <CreatorPayoutDialog
          creatorId={creatorId}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ChonkyButton size="sm" onClick={() => setIsDialogOpen(true)}>
          Gerar Payout (Fechamento)
        </ChonkyButton>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Período</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Vendas</TableHead>
              <TableHead className="text-right hidden md:table-cell">Comissões</TableHead>
              <TableHead className="text-right hidden lg:table-cell">Taxas</TableHead>
              <TableHead className="text-right font-bold">Líquido</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.map((payout: CreatorPayout) => (
              <TableRow key={payout.id}>
                <TableCell className="whitespace-nowrap">
                  <div className="text-sm font-medium">
                    {format(new Date(payout.periodStart), 'dd/MMM/yy', { locale: ptBR })}
                    {' - '}
                    {format(new Date(payout.periodEnd), 'dd/MMM/yy', { locale: ptBR })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Gerado em {format(new Date(payout.createdAt), 'dd/MM', { locale: ptBR })}
                  </div>
                </TableCell>
                <TableCell className="text-right hidden sm:table-cell">
                  {payout.totalSalesCount} pedidos
                  <div className="text-[10px] text-muted-foreground">
                    (
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                      payout.totalSalesAmount
                    )}
                    )
                  </div>
                </TableCell>
                <TableCell className="text-right hidden md:table-cell">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                    payout.totalCommission
                  )}
                </TableCell>
                <TableCell className="text-right text-red-600 dark:text-red-400 hidden lg:table-cell">
                  -
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                    payout.platformFeeAmount
                  )}
                  <div className="text-[10px] text-muted-foreground">
                    ({(payout.platformFeeRate * 100).toFixed(1)}%)
                  </div>
                </TableCell>
                <TableCell className="text-right font-bold text-green-600 dark:text-green-500 whitespace-nowrap">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                    payout.netAmount
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_COLORS[payout.status] || ''}>
                    {STATUS_LABELS[payout.status] || payout.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {meta && meta.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (page > 1) setPage(page - 1)
                }}
              />
            </PaginationItem>

            <PaginationItem>
              <span className="text-sm px-4">
                Página {page} de {meta.totalPages}
              </span>
            </PaginationItem>

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (page < meta.totalPages) setPage(page + 1)
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <CreatorPayoutDialog
        creatorId={creatorId}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  )
}
